import { Form, FormInstance, FormItemProps, Space, Spin, Upload } from 'antd';
import { getUrl, uploadMedia } from 'api/upload';
import FormItem from 'components/form/FormItem';
import FormList from 'components/form/FormList';
import ImageItem, {
  ImageItemProps,
} from 'components/form/FormUpload/ImageItem';
import { FormUploadStyle } from 'components/form/FormUpload/styles';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { RcFile } from 'antd/es/upload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { DynamicObjectType } from 'types/commons';

export interface IFormUploadProps
  extends FormItemProps,
    Pick<
      ImageItemProps,
      'formatHightLight' | 'customAction' | 'highlightName'
    > {
  type?: 'single' | 'array';
  label?: string | React.ReactNode;
  note?: string;
  accept?: string;
  formatValue?: (value: string | DynamicObjectType) => void;
  form?: FormInstance;
  onChange?: (value: string | DynamicObjectType) => void;

  value?: string | DynamicObjectType;
  required?: boolean;
}

export default function FormUpload({
  name,
  type = 'single',
  customAction,
  highlightName,
  formatHightLight,
  label,
  note,
  accept,
  formatValue,
  form,
  onChange,
  value,
  required,
}: IFormUploadProps) {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const customRequest = async ({ onSuccess, file }: UploadRequestOption) => {
    const responseS3 = await getUrl(
      (file as RcFile)?.name || '',
      (file as RcFile).type,
    );
    const xhr = await uploadMedia(responseS3.uploadUrl, file);
    if (xhr) {
      onSuccess(responseS3.name, xhr);
      onChange?.(responseS3.name);
    }
  };

  const onDel = (src) => {
    const val = form?.getFieldValue(name);
    form?.setFieldValue(
      name,
      type === 'single' ? null : val.filter((e) => e !== src),
    );
    setFileList(fileList.filter((file) => file.response !== src));
    onChange?.(type === 'single' ? null : val.filter((e) => e !== src));
  };

  const onChangeUpload = (e) => {
    setIsLoading(true);
    let isUploaded = true;
    setFileList(e.fileList);
    const formattedData = e.fileList.map((data) => {
      if (data.status !== 'done') {
        isUploaded = false;
      }
      return (data && data.response) || data.url;
    });

    isUploaded &&
      form &&
      form?.setFieldValue(
        name,
        type === 'single' ? formattedData[0] : [...formattedData],
      );
    isUploaded && setIsLoading(false);
  };

  useEffect(() => {
    if (
      Array.isArray(form?.getFieldValue(name)) &&
      form?.getFieldValue(name)?.length > 0 &&
      fileList?.length === 0 &&
      type === 'array'
    ) {
      const convert = form
        ?.getFieldValue(name)
        ?.map((item) => ({ uid: '-1', response: item, status: 'done' }));
      setFileList(convert);
    }
  }, [form?.getFieldValue(name)]);

  if (type !== 'single') {
    return (
      <div>
        <Form.Item
          shouldUpdate={(o, n) => {
            return get(o, name) !== get(n, name);
          }}
          label={
            <>
              {label}
              {required && (
                <span className="text-[#ff4d4f] inline-block text-sm leading-none ml-1">
                  *
                </span>
              )}
            </>
          }
        >
          {({ getFieldValue }) => {
            const value = formatValue
              ? formatValue(getFieldValue(name))
              : getFieldValue(name);
            return (
              <Space wrap>
                {Array.isArray(value)
                  ? value?.map((e) => (
                      <ImageItem
                        onDelete={onDel}
                        customAction={customAction}
                        key={e?.url || e}
                        src={e?.url || e}
                        highlightName={highlightName}
                        formatHightLight={formatHightLight}
                      />
                    ))
                  : value && (
                      <ImageItem
                        onDelete={onDel}
                        customAction={customAction}
                        src={value}
                        highlightName={highlightName}
                        formatHightLight={formatHightLight}
                      />
                    )}
                <FormUploadStyle>
                  <Upload.Dragger
                    customRequest={customRequest}
                    onChange={onChangeUpload}
                    showUploadList={false}
                    multiple
                    accept={accept}
                    fileList={Array.isArray(fileList) ? fileList : [fileList]}
                  >
                    {isLoading ? (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{
                              fontSize: 24,
                              color: '#575d5366',
                              marginBottom: '5px',
                            }}
                            spin
                          />
                        }
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faCamera}
                        fontSize={24}
                        style={{ color: '#B6B8C1' }}
                      />
                    )}
                    <div className="note">
                      {note || t('button.uploadImage')}
                    </div>
                  </Upload.Dragger>
                </FormUploadStyle>
              </Space>
            );
          }}
        </Form.Item>
        <div className="d-none">
          <FormList name={name}>
            <FormItem />
          </FormList>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="text-sm fw-600 mb-2">
        {label}
        {required && (
          <span className="text-[#ff4d4f] inline-block text-sm leading-none ml-1">
            *
          </span>
        )}
      </div>
      <Space>
        {value ? (
          <ImageItem
            onDelete={onDel}
            customAction={customAction}
            src={String(value)}
            highlightName={highlightName}
            formatHightLight={formatHightLight}
            type={type}
          />
        ) : (
          <FormUploadStyle>
            <Upload.Dragger
              customRequest={customRequest}
              onChange={onChangeUpload}
              showUploadList={false}
              accept={accept}
            >
              {isLoading ? (
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{
                        fontSize: 24,
                        color: '#575d5366',
                        marginBottom: '5px',
                      }}
                      spin
                    />
                  }
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCamera}
                  fontSize={24}
                  style={{ color: '#B6B8C1' }}
                />
              )}
              <div className="note">{note || t('button.uploadImage')}</div>
            </Upload.Dragger>
          </FormUploadStyle>
        )}
        <div className="d-none">
          <FormItem name={name} />
        </div>
      </Space>
    </div>
  );
}
